import Vue from 'vue';

import * as api from '@/api';
import { setLocale } from '@/i18n';
import createResource from '@/store/modules/resource';
import getAuthenticatedUser, { logout } from '@/api/AuthenticatedUser';
import { getGroupsWithTranslations } from '@/utils/groupUtils';
import { defineModule } from './module-utils';

const userApi = new api.UserClient();
const departmentApi = new api.DepartmentClient();
const groupApi = new api.GroupClient();

let userPromise = null;

const usersResource = createResource('USERS', () => userApi.getUsers(), []);

const userModule = defineModule({
  state: {
    groups: [],
    user: null as api.User,
    avatar: null,
    name: '',
    locale: 'en-US',
    ...usersResource.state,
    departments: [] as api.Department[],
    managedUsers: [] as api.UserDto[],
  },

  getters: {
    groups: (state) => state.groups,
    name: (state) => state.name,
    user: (state) => state.user,
    users: (state) => state.users,
    avatar: (state) => state.avatar,
    locale: (state) => state.locale,
    departments: (state) => state.departments,
    managedDepartments: (state) => state.departments.filter((d) => d.managerId === state?.user?.id),
    managedUsers: (state) => state.managedUsers,
    directManagedEmployees(state) {
      return state.managedUsers.filter((dept) => dept.directManagerId === state?.user?.id);
    },
  },

  mutations: {
    SET_USER_INFO: (state, payload) => {
      state.groups = payload.groups || state.groups;
      state.user = payload || state.user;
      state.name = payload.name || state.name;
      state.locale = payload.languageId || state.locale;
    },
    SET_USER: (state, currentUser) => {
      state.user = currentUser;
    },
    SET_GROUPS: (state, groups) => {
      Vue.set(state, 'groups', groups);
    },
    SET_LOCALE: (state, payload) => {
      state.locale = payload.locale;
    },
    ...usersResource.mutations,
  },

  actions: {
    SetLocale: async (context, payload) => {
      context.commit('SET_LOCALE', payload);
      await setLocale(payload.locale, context.state.user.tenantId);
    },
    GetUserInfo: ({ commit, state, dispatch }) => {
      try {
        if (userPromise) {
          return userPromise;
        }

        userPromise = getAuthenticatedUser()
          .then((authenticatedUser) => {
            if (!state.user) {
              return userApi.getUser({ userId: authenticatedUser.signInUserSession.idToken.payload['cognito:username'] });
            }
            return Promise.resolve(state.user);
          }, (err) => {
            console.warn('[vuex.user] GetUserInfo', err);
            commit('SET_USER_INFO', null);
            return Promise.reject();
          })
          .then((newUser) => {
            if (!state.user || newUser.id !== state.user.id) {
              // set new user state
              commit('SET_USER_INFO', newUser);
              const localePromise = dispatch('SetLocale', { locale: newUser.languageId });
              const routePromise = dispatch('GenerateRoutes', newUser, { root: true });
              return Promise.all([localePromise, routePromise]).then(() => newUser);
            }
            return Promise.resolve(newUser);
          }).finally(() => {
            userPromise = null;
          });
        return userPromise;
      } catch (err) {
        console.log(err);
        return Promise.resolve();
      }
    },
    SaveUser: async ({ commit }, user) => userApi.updateUser({ userId: user.id, body: user })
      .then(() => userApi.getUser({ userId: user.id }))
      .then((newUser) => {
        commit('SET_USER_INFO', newUser);
        userPromise = null;
        return Promise.resolve(newUser);
      }, (err) => {
        console.warn('[vuex.user] GetUserInfo', err);
        userPromise = null;
        return Promise.resolve();
      }),

    LogOut: async ({ commit }, noRedirect) => {
      try {
        console.log('[vuex.user] LogOut');
        await logout();
        commit('SET_USER', {});
        if (!noRedirect) {
          window.location.href = './';
        }
      } catch (err) {
        console.warn('[vuex.user] LogOut', err);
      }
    },
    ...usersResource.actions,
    async loadDepartments({ state }) {
      const withNameOrId = (d: api.Department) => d && (d.name || d.id);
      const byNameOrId = (a: api.Department, b: api.Department) => {
        const hasNameA = !!a.name;
        const hasNameB = !!b.name;

        if (hasNameA && !hasNameB) return -1;
        if (!hasNameA && hasNameB) return 1;

        const nameA = (a.name || a.id).toLowerCase();
        const nameB = (b.name || b.id).toLowerCase();

        return nameA.localeCompare(nameB);
      };
      const response = await departmentApi.getDepartments();
      state.departments = response.filter(withNameOrId).sort(byNameOrId);
      return response;
    },
    async loadManagedUsers({ state }) {
      const response = await userApi.getManagedUsers({
        enabled: true,
        sortCriteria: ['users_name:asc'] as any,
      });
      state.managedUsers = response;
      return response;
    },
    async loadGroups({ state }) {
      const response = await groupApi.getGroups({ isVisible: true });
      const groupsWithTranslations = await getGroupsWithTranslations(response, state.locale);
      state.groups = groupsWithTranslations;
      return groupsWithTranslations;
    },
  },
});

export default userModule;
